"use client";

import { useState } from "react";
import Link from "next/link";
import { Activity } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/shadcn/popover";
import { TwoStageTooltip } from "@/components/ui/common/TwoStageTooltip";
import { CompactUsageBar } from "@/components/ui/common/CompactUsageBar";
import { SubscriptionTier } from "@/components/ui/layouts/SubscriptionTier";
import { useUserUsage, TIER_LABELS } from "@/hooks/useUserUsage";
import { getUsagePercentage, getStatusColor, gpuWindowPct } from "@/lib/usage-utils";
import type { GpuQuotaWindow } from "@/hooks/useUserUsage";

function isGpuWindow(val: unknown): val is GpuQuotaWindow {
  return (
    typeof val === "object" && val !== null && "used" in val && "limit" in val && "remaining" in val
  );
}

function getStatusLabel(pct: number, tier: string): string {
  if (pct > 90) return "Fair-use limit reached";
  if (pct > 75) return "Nearing rate limits";
  return tier === "starter" ? "Running at standard speed" : "Running at full speed";
}

function getStatusHint(pct: number, tier: string): string {
  if (pct > 75 && tier === "starter") return "Upgrade for higher limits";
  if (tier === "starter") return "Upgrade for full speed";
  return "Click for usage details";
}

export function NavbarQuotaIndicator() {
  const { data } = useUserUsage(15_000);
  const [isOpen, setIsOpen] = useState(false);

  if (!data) return null;

  const pct = getUsagePercentage(data);
  const statusColor = getStatusColor(pct, data.tier);
  const planLabel = (TIER_LABELS[data.tier] ?? data.tier) as "Starter" | "Plus" | "Max" | "Ultra";

  const fourHour = data.gpuQuota?.fourHour;
  const sevenDay = data.gpuQuota?.sevenDay;
  const monthly = data.gpuQuota?.monthly;
  const hasGpuQuota = isGpuWindow(fourHour) || isGpuWindow(sevenDay) || isGpuWindow(monthly);
  const maxGpuPct = Math.max(gpuWindowPct(fourHour), gpuWindowPct(sevenDay), gpuWindowPct(monthly));

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TwoStageTooltip
        label={getStatusLabel(pct, data.tier)}
        hint={getStatusHint(pct, data.tier)}
        side="bottom"
        align="end"
        disabled={isOpen}
      >
        <PopoverTrigger asChild>
          <button
            className={`relative flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent transition-colors ${statusColor}`}
            aria-label="Usage & quota"
          >
            <Activity className="h-5 w-5" />
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[6px] font-semibold uppercase tracking-widest leading-none select-none opacity-80">
              {planLabel}
            </span>
          </button>
        </PopoverTrigger>
      </TwoStageTooltip>

      <PopoverContent side="bottom" align="end" sideOffset={8} className="w-72">
        <div className="space-y-4">
          {/* Subscription tier badge */}
          <SubscriptionTier plan={planLabel} />

          {/* GPU usage */}
          {hasGpuQuota && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                GPU Usage
              </h3>
              {isGpuWindow(fourHour) && (
                <CompactUsageBar
                  label="Session"
                  used={fourHour.used}
                  limit={fourHour.limit}
                  resetInSeconds={fourHour.resetInSeconds}
                />
              )}
              {isGpuWindow(sevenDay) && gpuWindowPct(sevenDay) >= 50 && (
                <CompactUsageBar
                  label="Week"
                  used={sevenDay.used}
                  limit={sevenDay.limit}
                  resetInSeconds={sevenDay.resetInSeconds}
                />
              )}
              {isGpuWindow(monthly) && gpuWindowPct(monthly) >= 50 && (
                <CompactUsageBar
                  label="Month"
                  used={monthly.used}
                  limit={monthly.limit}
                  resetInSeconds={monthly.resetInSeconds}
                />
              )}
            </div>
          )}

          {/* Upgrade CTA */}
          {data.tier !== "ultra" &&
            (gpuWindowPct(fourHour) >= 90 ||
              gpuWindowPct(sevenDay) >= 90 ||
              gpuWindowPct(monthly) >= 90) && (
              <div className="pt-2 border-t border-border space-y-1.5">
                <p className="text-xs text-muted-foreground">
                  {maxGpuPct >= 100
                    ? "You've used your fast generation quota for now. Continuing at standard speed."
                    : "You've almost used your fast generation quota for now. Continuing at standard speed."}
                </p>
                <Link
                  href="/profile#subscription"
                  className="text-xs text-violet-400 hover:text-violet-300 font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Upgrade for more fast generations →
                </Link>
              </div>
            )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
